/**
 * Pricing model utility for Fox River AI
 * This module provides consistent pricing calculations for both quick estimates and detailed proposals
 */

/**
 * Classify project complexity based on requirements
 * @param {Object} context - Project context information
 * @returns {String} - Complexity level: 'basic', 'standard', or 'complex'
 */
export const classifyProjectComplexity = (context) => {
  const { 
    documentCount, 
    skuCount, 
    productCount, 
    warehouseCount,
    needsMemory,
    needsTools,
    needsOrchestration,
    specializedUseCase,
    integrationType,
    dataSize,
    // New features
    hasIoTIntegration,
    hasRealtimeRequirement,
    hasComplianceRequirements,
    isEnterpriseScale,
    integrationCount,
    // Force complexity (for consistency)
    forceComplexity
  } = context;
  
  // If complexity is explicitly provided, use it
  if (forceComplexity && ['basic', 'standard', 'complex'].includes(forceComplexity)) {
    return forceComplexity;
  }

  // Default to standard complexity
  let complexity = 'standard';
  
  // Count advanced features to determine complexity
  let complexityScore = 0;
  
  // Add points for basic features
  if (needsMemory) complexityScore += 1;
  if (needsTools) complexityScore += 1;
  if (needsOrchestration) complexityScore += 2;
  if (integrationType) complexityScore += 1;
  
  // Add points for advanced features
  if (hasIoTIntegration) complexityScore += 2;
  if (hasRealtimeRequirement) complexityScore += 1;
  if (hasComplianceRequirements) complexityScore += 2;
  if (isEnterpriseScale) complexityScore += 2;
  
  // Add points for multiple integrations
  if (integrationCount && integrationCount > 1) {
    complexityScore += Math.min(3, integrationCount - 1); // Up to 3 additional points
  }
  
  // Add points for specialized use cases
  if (specializedUseCase === 'legal_nlp' || specializedUseCase === 'medical_nlp') {
    complexityScore += 2;
  } else if (specializedUseCase === 'financial_nlp' || specializedUseCase === 'medical_imaging') {
    complexityScore += 1;
  }
  
  // Add points for data volume
  if (documentCount) {
    if (documentCount > 3000) complexityScore += 2;
    else if (documentCount > 500) complexityScore += 1;
  }
  
  if (skuCount) {
    if (skuCount > 3000) complexityScore += 2;
    else if (skuCount > 500) complexityScore += 1;
  }
  
  if (productCount) {
    if (productCount > 3000) complexityScore += 2;
    else if (productCount > 500) complexityScore += 1;
  }
  
  // Add points for warehouse count
  if (warehouseCount && warehouseCount > 1) {
    if (warehouseCount > 3) complexityScore += 2;
    else complexityScore += 1;
  }
  
  // Add points for data size
  if (dataSize) {
    if (dataSize.unit === 'TB' && dataSize.size > 1) complexityScore += 2;
    else if (dataSize.unit === 'GB' && dataSize.size > 500) complexityScore += 2;
    else if (dataSize.unit === 'GB' && dataSize.size > 100) complexityScore += 1;
  }
  
  // Determine complexity based on score
  if (complexityScore <= 1) {
    complexity = 'basic';
  } else if (complexityScore <= 4) {
    complexity = 'standard';
  } else {
    complexity = 'complex';
  }
  
  // Special case overrides
  // If multiple advanced features are present, always consider it complex
  if ((needsMemory && needsTools && needsOrchestration) || 
      (hasIoTIntegration && hasRealtimeRequirement) || 
      (hasComplianceRequirements && specializedUseCase === 'medical_nlp') ||
      (hasComplianceRequirements && specializedUseCase === 'legal_nlp')) {
    complexity = 'complex';
  }
  
  // If no special features at all, consider it basic
  if (!needsMemory && !needsTools && !needsOrchestration && 
      !integrationType && !specializedUseCase && 
      !hasIoTIntegration && !hasRealtimeRequirement && 
      !hasComplianceRequirements && !isEnterpriseScale && 
      (!documentCount || documentCount < 300) && 
      (!skuCount || skuCount < 100) && 
      (!productCount || productCount < 100) && 
      (!warehouseCount || warehouseCount === 1)) {
    complexity = 'basic';
  }
  
  return complexity;
};

/**
 * Calculate project costs based on project type and complexity
 * @param {Object} context - Project context information
 * @returns {Object} - Cost breakdown including development and ongoing costs
 */
export const calculateProjectCosts = (context) => {
  const { 
    projectType, 
    useSelfServiceWizard,
    needsMemory,
    needsTools,
    needsOrchestration,
    hasIoTIntegration,
    hasRealtimeRequirement,
    hasComplianceRequirements,
    isEnterpriseScale,
    integrationCount,
    specializedUseCase
  } = context;
  
  const complexity = classifyProjectComplexity(context);
  
  // Base costs by project type and complexity
  const baseCosts = {
    inventory: {
      basic: { dev: 15000, monthly: 1000 },
      standard: { dev: 30000, monthly: 2000 },
      complex: { dev: 60000, monthly: 4000 }
    },
    document_ai: {
      basic: { dev: 20000, monthly: 1500 },
      standard: { dev: 40000, monthly: 3000 },
      complex: { dev: 80000, monthly: 6000 }
    },
    computer_vision: {
      basic: { dev: 25000, monthly: 2000 },
      standard: { dev: 50000, monthly: 4000 },
      complex: { dev: 100000, monthly: 8000 }
    },
    genai_agent: {
      basic: { dev: 20000, monthly: 1500 },
      standard: { dev: 40000, monthly: 3000 },
      complex: { dev: 80000, monthly: 6000 }
    },
    chatbot: {
      basic: { dev: 15000, monthly: 1000 },
      standard: { dev: 30000, monthly: 2000 },
      complex: { dev: 60000, monthly: 4000 }
    },
    analytics: {
      basic: { dev: 20000, monthly: 1500 },
      standard: { dev: 40000, monthly: 3000 },
      complex: { dev: 80000, monthly: 6000 }
    },
    recommendation: {
      basic: { dev: 15000, monthly: 1000 },
      standard: { dev: 30000, monthly: 2000 },
      complex: { dev: 60000, monthly: 4000 }
    },
    // Default for any other project type
    default: {
      basic: { dev: 20000, monthly: 1500 },
      standard: { dev: 40000, monthly: 3000 },
      complex: { dev: 80000, monthly: 6000 }
    }
  };
  
  // Get base costs for this project type and complexity
  const costs = baseCosts[projectType] || baseCosts.default;
  let baseDev = costs[complexity].dev;
  let baseMonthly = costs[complexity].monthly;
  
  // Apply feature-specific cost adjustments
  let costMultiplier = 1.0;
  
  // IoT integration is expensive
  if (hasIoTIntegration) {
    costMultiplier += 0.2;
    baseMonthly += 500; // IoT adds significant monthly costs
  }
  
  // Real-time requirements add costs
  if (hasRealtimeRequirement) {
    costMultiplier += 0.15;
    baseMonthly += 300; // Real-time processing adds monthly costs
  }
  
  // Compliance requirements add costs
  if (hasComplianceRequirements) {
    costMultiplier += 0.15;
    baseMonthly += 400; // Compliance monitoring adds monthly costs
  }
  
  // Multiple integrations add costs
  if (integrationCount && integrationCount > 1) {
    costMultiplier += 0.1 * Math.min(3, integrationCount - 1);
    baseMonthly += 200 * Math.min(3, integrationCount - 1);
  }
  
  // Integration type affects costs
  if (context.integrationType && context.integrationType !== 'none') {
    // Different integration types have different complexity
    switch (context.integrationType) {
      case 'ERP':
        costMultiplier += 0.15;
        baseMonthly += 350;
        break;
      case 'CRM':
        costMultiplier += 0.1;
        baseMonthly += 250;
        break;
      case 'POS':
        costMultiplier += 0.12;
        baseMonthly += 300;
        break;
      case 'general':
        costMultiplier += 0.08;
        baseMonthly += 200;
        break;
      default:
        break;
    }
  }
  
  // Specialized use cases add costs
  if (specializedUseCase === 'legal_nlp' || specializedUseCase === 'medical_nlp') {
    costMultiplier += 0.2;
    baseMonthly += 500;
  } else if (specializedUseCase === 'financial_nlp' || specializedUseCase === 'medical_imaging') {
    costMultiplier += 0.1;
    baseMonthly += 300;
  }
  
  // Advanced agent features add costs
  if (needsMemory) costMultiplier += 0.05;
  if (needsTools) costMultiplier += 0.1;
  if (needsOrchestration) costMultiplier += 0.15;
  
  // Enterprise scale adds costs
  if (isEnterpriseScale) {
    costMultiplier += 0.2;
    baseMonthly += 1000;
  }
  
  // Apply the cost multiplier
  baseDev = Math.round(baseDev * costMultiplier);
  
  // Apply Self Service Build Wizard discount if enabled
  const wizardDiscount = useSelfServiceWizard ? 0.3 : 1.0; // 70% discount with wizard
  
  // Calculate final costs with wizard discount
  const devCost = Math.round(baseDev * wizardDiscount);
  
  // Calculate infrastructure cost (no discount)
  const infrastructureCost = Math.round(baseMonthly * 0.3); // 30% for infrastructure
  
  // Calculate discounted portion of monthly cost (maintenance and model updates)
  const discountedMonthlyPortion = baseMonthly * 0.7; // 70% for maintenance and model updates
  const discountedMonthlyCost = Math.round(discountedMonthlyPortion * wizardDiscount);
  
  // Total monthly cost combines non-discounted infrastructure with discounted maintenance/updates
  const monthlyCost = infrastructureCost + discountedMonthlyCost;
  
  // Calculate cost ranges (±15%)
  const devCostMin = Math.round(devCost * 0.85);
  const devCostMax = Math.round(devCost * 1.15);
  const monthlyCostMin = Math.round(monthlyCost * 0.85);
  const monthlyCostMax = Math.round(monthlyCost * 1.15);
  
  // Break down development costs into phases
  const discoveryDesignCost = Math.round(devCost * 0.25); // 25% for discovery and design
  const implementationCost = Math.round(devCost * 0.55); // 55% for implementation
  const testingDeploymentCost = Math.round(devCost * 0.20); // 20% for testing and deployment
  
  // Only maintenance and model updates get the wizard discount
  const maintenanceCost = Math.round(discountedMonthlyPortion * 0.7 * wizardDiscount); // 70% of the discounted portion
  const modelUpdatesCost = Math.round(discountedMonthlyPortion * 0.3 * wizardDiscount); // 30% of the discounted portion
  
  return {
    complexity,
    totalDevCost: devCost,
    totalDevRange: { min: devCostMin, max: devCostMax },
    totalMonthly: monthlyCost,
    totalMonthlyRange: { min: monthlyCostMin, max: monthlyCostMax },
    breakdown: {
      development: {
        discoveryDesign: { min: Math.round(discoveryDesignCost * 0.85), max: Math.round(discoveryDesignCost * 1.15) },
        implementation: { min: Math.round(implementationCost * 0.85), max: Math.round(implementationCost * 1.15) },
        testingDeployment: { min: Math.round(testingDeploymentCost * 0.85), max: Math.round(testingDeploymentCost * 1.15) }
      },
      monthly: {
        infrastructure: { min: Math.round(infrastructureCost * 0.85), max: Math.round(infrastructureCost * 1.15) },
        maintenance: { min: Math.round(maintenanceCost * 0.85), max: Math.round(maintenanceCost * 1.15) },
        modelUpdates: { min: Math.round(modelUpdatesCost * 0.85), max: Math.round(modelUpdatesCost * 1.15) }
      }
    }
  };
};

/**
 * Generate a project description based on complexity
 * @param {String} projectType - Type of project
 * @param {String} complexity - Project complexity level
 * @returns {String} - Project description
 */
export const getProjectDescription = (projectType, complexity) => {
  const descriptions = {
    inventory: {
      basic: "Basic inventory tracking system with core features",
      standard: "Standard inventory management system with forecasting",
      complex: "Enterprise-grade inventory solution with multi-location support"
    },
    document_ai: {
      basic: "Document processing system for basic extraction needs",
      standard: "Comprehensive document analysis with NLP capabilities",
      complex: "Advanced document intelligence platform with specialized features"
    },
    computer_vision: {
      basic: "Basic computer vision system for simple object detection",
      standard: "Advanced image analysis with multiple detection capabilities",
      complex: "Enterprise computer vision platform with specialized algorithms"
    },
    genai_agent: {
      basic: "Simple AI assistant with basic question answering",
      standard: "Intelligent agent with memory and basic tool usage",
      complex: "Advanced AI agent with orchestration and multiple integrations"
    },
    chatbot: {
      basic: "Simple chatbot for customer inquiries",
      standard: "Intelligent conversational agent with context awareness",
      complex: "Enterprise-grade virtual assistant with multiple integrations"
    },
    analytics: {
      basic: "Basic data analytics dashboard with key metrics",
      standard: "Comprehensive analytics platform with predictive capabilities",
      complex: "Advanced analytics solution with custom ML models"
    },
    recommendation: {
      basic: "Simple recommendation engine for basic suggestions",
      standard: "Personalized recommendation system with user behavior analysis",
      complex: "Advanced recommendation platform with multi-factor algorithms"
    },
    default: {
      basic: "Basic AI solution with core functionality",
      standard: "Comprehensive AI platform with advanced features",
      complex: "Enterprise-grade AI solution with specialized capabilities"
    }
  };
  
  return (descriptions[projectType] || descriptions.default)[complexity];
};

/**
 * Generate implementation timeline based on project complexity and features
 * @param {String} complexity - Project complexity level
 * @param {Object} context - Additional project context
 * @returns {Object} - Timeline breakdown in weeks
 */
export const getImplementationTimeline = (complexity, context = {}) => {
  // Base timeline by complexity
  const baseTimelines = {
    basic: {
      discovery: 1,
      design: 1,
      development: 4,
      testing: 1,
      deployment: 1
    },
    standard: {
      discovery: 2,
      design: 2,
      development: 8,
      testing: 2,
      deployment: 1
    },
    complex: {
      discovery: 3,
      design: 4,
      development: 12,
      testing: 4,
      deployment: 2
    }
  };
  
  let timeline = baseTimelines[complexity] || baseTimelines.standard;
  
  // Adjust timeline based on specific features
  const {
    needsOrchestration,
    hasIoTIntegration,
    hasRealtimeRequirement,
    hasComplianceRequirements,
    integrationCount,
    specializedUseCase
  } = context;
  
  // Add time for IoT integration
  if (hasIoTIntegration) {
    timeline.discovery += 1;
    timeline.design += 1;
    timeline.development += 2;
    timeline.testing += 1;
  }
  
  // Add time for real-time requirements
  if (hasRealtimeRequirement) {
    timeline.design += 1;
    timeline.development += 1;
    timeline.testing += 1;
  }
  
  // Add time for compliance requirements
  if (hasComplianceRequirements) {
    timeline.discovery += 1;
    timeline.testing += 1;
  }
  
  // Add time for multiple integrations
  if (integrationCount && integrationCount > 1) {
    timeline.development += Math.min(3, integrationCount - 1);
    timeline.testing += Math.min(2, integrationCount - 1);
  }
  
  // Add time for specialized use cases
  if (specializedUseCase === 'legal_nlp' || specializedUseCase === 'medical_nlp') {
    timeline.discovery += 1;
    timeline.development += 2;
    timeline.testing += 1;
  }
  
  // Add time for orchestration
  if (needsOrchestration) {
    timeline.design += 1;
    timeline.development += 2;
  }
  
  // Calculate total
  timeline.total = timeline.discovery + timeline.design + timeline.development + 
                   timeline.testing + timeline.deployment;
  
  return timeline;
};